import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Add20Regular,
  Filter20Regular,
  ArrowDownload20Regular,
  MoreVertical20Regular,
  Location20Regular,
  Calendar20Regular,
} from "@fluentui/react-icons";
import {
  ApiError,
  getWorks,
  type Work,
} from "../../obras/obrasApi";
import { NovaObraModal } from "../../components/modals/NovaObraModal";
import { APP_CURRENCY, APP_LOCALE } from "../../config/locale";

interface WorkView {
  id: number;
  ref: string;
  nome: string;
  cliente: string;
  localizacao: string;
  dataInicio: string;
  prazo: string;
  progresso: number;
  valorTotal?: number | null;
  valorGasto?: number | null;
  estado: string;
  createdAt: string;
}

const formatDate = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar as obras.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar as obras.";
};

const mapWorkToView = (work: Work): WorkView => {
  return {
    id: work.id,
    ref: work.code,
    nome: work.name,
    cliente: work.clientName,
    localizacao: work.location?.trim() || "—",
    dataInicio: work.startDate ? formatDate(work.startDate) : "—",
    prazo: work.endDate ? formatDate(work.endDate) : "—",
    progresso: Math.round(work.progressPercent ?? 0),
    valorTotal: work.totalValue ?? null,
    valorGasto: work.spentValue ?? null,
    estado: work.statusName?.trim() || "Em Curso",
    createdAt: work.created,
  };
};

const formatCurrency = (value?: number | null): string => {
  if (value == null) return "—";

  const formatted = new Intl.NumberFormat(APP_LOCALE, {
    style: "currency",
    currency: APP_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  return formatted.replace(/\./g, " ");
};

export function ObrasView() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"list" | "cards">(
    "cards",
  );
  const [works, setWorks] = useState<WorkView[]>([]);
  const [isNovaObraOpen, setIsNovaObraOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    let isMounted = true;

    const loadWorks = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await getWorks();
        if (!isMounted) return;

        const mapped = response.map(mapWorkToView);
        setWorks(mapped);
      } catch (error) {
        if (!isMounted) return;
        setWorks([]);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadWorks();

    return () => {
      isMounted = false;
    };
  }, []);

  const renderCardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {works.map((obra) => {
        const progresso = Math.max(0, Math.min(100, obra.progresso));

        return (
          <div
            key={obra.id}
            onClick={() => navigate(`/obras/${obra.id}`)}
            className="bg-card border border-border p-4 md:p-6 hover:shadow-md transition-all cursor-pointer"
            style={{
              borderRadius: "var(--radius-card)",
              boxShadow: "var(--elevation-sm)",
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    {obra.ref}
                  </span>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded ${
                      obra.estado === "Em Curso"
                        ? "bg-primary/10 text-primary"
                        : "bg-chart-1/10 text-chart-1"
                    }`}
                    style={{
                      fontSize: "var(--text-caption)",
                      borderRadius: "var(--radius)",
                    }}
                  >
                    {obra.estado}
                  </span>
                </div>
                <h4 className="truncate">{obra.nome}</h4>
                <p className="text-secondary-text mt-1 truncate">
                  {obra.cliente}
                </p>
              </div>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                }}
                className="p-2 hover:bg-accent rounded min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                style={{ borderRadius: "var(--radius)" }}
              >
                <MoreVertical20Regular className="w-4 h-4 text-secondary-text" />
              </button>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-caption)" }}
                >
                  Progresso
                </span>
                <span
                  style={{
                    fontWeight: "var(--font-weight-semi-bold)",
                    fontSize: "var(--text-caption)",
                  }}
                >
                  {progresso}%
                </span>
              </div>
              <div
                className="w-full bg-muted rounded-full h-2"
                style={{ borderRadius: "var(--radius)" }}
              >
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{
                    width: `${progresso}%`,
                    borderRadius: "var(--radius)",
                  }}
                ></div>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-secondary-text">
                <Location20Regular className="w-4 h-4 flex-shrink-0" />
                <span style={{ fontSize: "var(--text-caption)" }}>
                  {obra.localizacao}
                </span>
              </div>
              <div className="flex items-center gap-2 text-secondary-text">
                <Calendar20Regular className="w-4 h-4 flex-shrink-0" />
                <span
                  style={{ fontSize: "var(--text-caption)" }}
                  className="truncate"
                >
                  {obra.dataInicio} - {obra.prazo}
                </span>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Valor Total
                  </p>
                  <p
                    style={{
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    {formatCurrency(obra.valorTotal)}
                  </p>
                </div>
                <div className="text-right">
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Gasto
                  </p>
                  <p
                    style={{
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    {formatCurrency(obra.valorGasto)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderListView = () => (
    <div>
      <div
        className="hidden md:block bg-card border border-border overflow-hidden"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted">
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Ref.
              </th>
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Nome
              </th>
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Cliente
              </th>
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Progresso
              </th>
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Prazo
              </th>
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Valor Total
              </th>
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Estado
              </th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {works.map((obra) => {
              const progresso = Math.max(
                0,
                Math.min(100, obra.progresso),
              );

              return (
                <tr
                  key={obra.id}
                  onClick={() => navigate(`/obras/${obra.id}`)}
                  className="border-b border-border hover:bg-accent/30 transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4">{obra.ref}</td>
                  <td
                    className="px-6 py-4"
                    style={{
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    {obra.nome}
                  </td>
                  <td className="px-6 py-4">{obra.cliente}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-24 bg-muted rounded-full h-2"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{
                            width: `${progresso}%`,
                            borderRadius: "var(--radius)",
                          }}
                        ></div>
                      </div>
                      <span
                        className="text-secondary-text"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        {progresso}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-secondary-text">
                    {obra.prazo}
                  </td>
                  <td
                    className="px-6 py-4"
                    style={{
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    {formatCurrency(obra.valorTotal)}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 rounded ${
                        obra.estado === "Em Curso"
                          ? "bg-primary/10 text-primary"
                          : "bg-chart-1/10 text-chart-1"
                      }`}
                      style={{
                        fontSize: "var(--text-caption)",
                        borderRadius: "var(--radius)",
                      }}
                    >
                      {obra.estado}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      className="p-1 hover:bg-accent rounded"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <MoreVertical20Regular className="w-4 h-4 text-secondary-text" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">
        {works.map((obra) => {
          const progresso = Math.max(
            0,
            Math.min(100, obra.progresso),
          );

          return (
            <div
              key={obra.id}
              onClick={() => navigate(`/obras/${obra.id}`)}
              className="bg-card border border-border p-4 space-y-3 cursor-pointer"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p
                    style={{
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                    className="truncate"
                  >
                    {obra.nome}
                  </p>
                  <p className="text-secondary-text mt-1 truncate">
                    {obra.cliente}
                  </p>
                  <p
                    className="text-secondary-text mt-0.5"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    {obra.ref}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  className="p-2 hover:bg-accent rounded min-w-[44px] min-h-[44px] flex items-center justify-center"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  <MoreVertical20Regular className="w-4 h-4 text-secondary-text" />
                </button>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Progresso
                  </span>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded ${
                      obra.estado === "Em Curso"
                        ? "bg-primary/10 text-primary"
                        : "bg-chart-1/10 text-chart-1"
                    }`}
                    style={{
                      fontSize: "var(--text-caption)",
                      borderRadius: "var(--radius)",
                    }}
                  >
                    {obra.estado}
                  </span>
                </div>
                <div
                  className="w-full bg-muted rounded-full h-2 mb-1"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{
                      width: `${progresso}%`,
                      borderRadius: "var(--radius)",
                    }}
                  ></div>
                </div>
                <span
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-caption)" }}
                >
                  {progresso}%
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
                <div>
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Valor Total
                  </p>
                  <p
                    className="mt-0.5"
                    style={{
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    {formatCurrency(obra.valorTotal)}
                  </p>
                </div>
                <div>
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Gasto
                  </p>
                  <p
                    className="mt-0.5"
                    style={{
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    {formatCurrency(obra.valorGasto)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2>Obras</h2>
          <p className="text-secondary-text mt-1">
            Gestão de obras em curso
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="hidden sm:flex items-center bg-card border border-border rounded"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            <button
              onClick={() => setViewMode("cards")}
              className={`px-3 py-2 rounded transition-colors min-h-[44px] ${
                viewMode === "cards"
                  ? "bg-accent text-accent-foreground"
                  : "text-secondary-text hover:text-foreground"
              }`}
              style={{ borderRadius: "var(--radius-button)" }}
            >
              Cards
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`px-3 py-2 rounded transition-colors min-h-[44px] ${
                viewMode === "list"
                  ? "bg-accent text-accent-foreground"
                  : "text-secondary-text hover:text-foreground"
              }`}
              style={{ borderRadius: "var(--radius-button)" }}
            >
              Lista
            </button>
          </div>
          <button
            type="button"
            className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            <Filter20Regular className="w-4 h-4" />
            <span className="hidden sm:inline">Filtrar</span>
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            <ArrowDownload20Regular className="w-4 h-4" />
            <span className="hidden sm:inline">Exportar</span>
          </button>
          <button
            type="button"
            onClick={() => setIsNovaObraOpen(true)}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
            style={{
              borderRadius: "var(--radius-button)",
              height: "var(--button-height)",
            }}
            title="Criar uma nova obra, com ou sem orçamento."
          >
            <Add20Regular className="w-4 h-4" />
            <span className="hidden sm:inline">Nova Obra</span>
            <span className="sm:hidden">Nova</span>
          </button>
        </div>
      </div>

      {loadError && (
        <div
          className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {loadError}
        </div>
      )}

      {isLoading && (
        <div
          className="border border-border bg-card px-4 py-3 text-secondary-text"
          style={{ borderRadius: "var(--radius)" }}
        >
          A carregar obras...
        </div>
      )}

      {!isLoading && !loadError && works.length === 0 && (
        <div
          className="border border-border bg-card px-4 py-6 text-secondary-text text-center"
          style={{ borderRadius: "var(--radius)" }}
        >
          Sem obras para apresentar.
        </div>
      )}

      {!isLoading && !loadError && works.length > 0 &&
        (viewMode === "cards" ? renderCardView() : renderListView())}

      <NovaObraModal
        isOpen={isNovaObraOpen}
        onClose={() => setIsNovaObraOpen(false)}
        onCreated={(work) => {
          setIsNovaObraOpen(false);
          navigate(`/obras/${work.id}`);
        }}
      />
    </div>
  );
}
